import React, { useState, useEffect, useMemo } from 'react';
import { CONTENEDOR, PAGINA, PantallaHeader, BotonHerramienta } from './ui/Pantalla';
import api from '../services/api';
import { toast } from 'react-hot-toast';
import { LuRefreshCw, LuMessageCircle, LuWrench, LuCheck, LuBuilding2 } from 'react-icons/lu';
import { fechaAR } from '../utils/dateUtils';
import { leerOferta, mensajeOferta, ofrecerPorWhatsApp, haceCuanto, OFERTA_DEFECTO } from '../utils/ofertaMantenimiento';
import OfertaConfig from './radar/OfertaConfig';

// Radar (3-oct-2026): agrupado por cliente — "Sanatorio Güemes · 3 equipos vencidos"
// con UN botón para crear el trabajo de todos juntos.
// 20-oct-2026: misma regla que el mapa (por vencer / vencido), WhatsApp con la oferta armada (lo manda
// el admin, la app no escribe sola) y marca "Ofrecido" para no repetir. Si no responde, a los 30 días
// vuelve a "Para ofrecer".
const COLOR = { VENCIDO: '#DC2626', POR_VENCER: '#D48800' };
const CHIPS = [
    { id: 'ofrecer', label: 'Para ofrecer', pasa: g => !g.ofrecidoReciente },
    { id: 'VENCIDO', label: 'Vencidos', pasa: g => g.estado === 'VENCIDO' },
    { id: 'POR_VENCER', label: 'Por vencer', pasa: g => g.estado === 'POR_VENCER' },
    { id: 'ofrecidos', label: 'Ofrecidos', pasa: g => g.ofrecidoReciente },
];

export default function RadarMantenimiento({ onCrearTrabajo }) {
    const [alertas, setAlertas]   = useState([]);
    const [cargando, setCargando] = useState(true);
    const [chip, setChip]         = useState('ofrecer');
    const [oferta, setOferta]     = useState(OFERTA_DEFECTO);
    const [abiertos, setAbiertos] = useState({});   // clientes con la lista de equipos completa

    useEffect(() => { cargarAlertas(); leerOferta().then(setOferta); }, []);

    const cargarAlertas = async () => {
        setCargando(true);
        try {
            const res = await api.get('/radar/alertas');
            setAlertas(res.data || []);
        } catch {
            toast.error('Error al generar el Radar');
        } finally {
            setCargando(false);
        }
    };

    const grupos = useMemo(() => {
        const m = new Map();
        alertas.forEach(a => {
            const k = a.clienteId || a.clienteNombre;
            if (!m.has(k)) m.set(k, { key: String(k), cliente: a.clienteNombre, clienteId: a.clienteId, telefono: a.clienteTelefono,
                conPortal: a.conPortal, ofrecidoEn: a.ofrecidoEn, ofrecidoReciente: a.ofrecidoReciente,
                items: [], filtros: 0, mesesMax: 0, estado: 'POR_VENCER', sedeId: a.sedeId });
            const g = m.get(k);
            g.items.push(a);
            if (a.tipoAlerta === 'FILTRO') g.filtros++;
            if (a.estado === 'VENCIDO') g.estado = 'VENCIDO';
            g.mesesMax = Math.max(g.mesesMax, a.meses || 0);
        });
        return [...m.values()].sort((a, b) => (a.estado === b.estado ? 0 : a.estado === 'VENCIDO' ? -1 : 1)
            || b.items.length - a.items.length || b.mesesMax - a.mesesMax);
    }, [alertas]);

    const cuenta = (id) => grupos.filter(CHIPS.find(c => c.id === id).pasa).length;
    const visibles = grupos.filter(CHIPS.find(c => c.id === chip).pasa);

    const ofrecer = async (g) => {
        if (!g.telefono) toast('Este cliente no tiene teléfono: elegí el contacto en WhatsApp.');
        const tipo = g.filtros > 0 ? 'FILTRO' : 'SANITIZACION';
        const deTipo = g.items.filter(a => a.tipoAlerta === tipo);
        // La fecha más vieja de ese tipo (la que más urge)
        const fechaUltimo = deTipo.map(a => (tipo === 'FILTRO' && a.fechaUltimoFiltro) || a.fechaUltimoServicio).sort()[0];
        const en = await ofrecerPorWhatsApp({ telefono: g.telefono, clienteId: g.clienteId, sedeId: g.items.length === 1 ? g.sedeId : null,
            tipo, origen: 'RADAR', mensaje: mensajeOferta({ cliente: g.cliente, tipo, fechaUltimo, oferta }) });
        if (en) setAlertas(as => as.map(a => (a.clienteId === g.clienteId ? { ...a, ofrecidoEn: en, ofrecidoReciente: true } : a)));
    };

    if (cargando) return (
        <div className="min-h-screen bg-page flex items-center justify-center p-5 transition-colors">
            <p className="font-black text-muted animate-pulse uppercase text-sm tracking-widest">Escaneando base de datos...</p>
        </div>
    );

    return (
        <div className={PAGINA}>
            <div className={CONTENEDOR}>
                <PantallaHeader titulo="Radar" subtitulo="Para ofrecer mantenimiento preventivo">
                    <BotonHerramienta icono={LuRefreshCw} onClick={cargarAlertas} title="Actualizar" />
                </PantallaHeader>

                <OfertaConfig oferta={oferta} onCambio={setOferta} />

                {grupos.length > 0 && (
                    <div className="flex gap-1.5 overflow-x-auto pb-1 -mx-1 px-1 mb-3">
                        {CHIPS.map(c => (
                            <button key={c.id} type="button" onClick={() => setChip(c.id)}
                                className={`shrink-0 h-9 px-3 rounded-full text-label font-black active:scale-95 ${chip === c.id ? 'bg-ink text-page' : 'bg-chip text-secondary'}`}>
                                {c.label} <span className="opacity-60">{cuenta(c.id)}</span>
                            </button>
                        ))}
                    </div>
                )}

                {grupos.length === 0 ? (
                    <div className="text-center p-10 bg-card rounded-[2rem] border border-black/[0.07] dark:border-white/[0.07]">
                        <p className="text-brand-red font-black text-xl uppercase">Todo al día</p>
                        <p className="text-muted text-caption font-bold uppercase mt-2">No hay dispensers por vencer.</p>
                    </div>
                ) : visibles.length === 0 ? (
                    <p className="text-center text-caption text-muted py-8">{chip === 'ofrecer' ? 'Ya les ofreciste a todos. Los que no respondan vuelven acá a los 30 días.' : 'No hay ninguno acá.'}</p>
                ) : (
                    <div className="flex flex-col gap-3">
                        {visibles.map(g => (
                            <div key={g.key} className="bg-card rounded-2xl border border-black/[0.07] dark:border-white/[0.07] overflow-hidden">
                                <div className="p-4 space-y-3">
                                    <div className="flex items-start justify-between gap-3">
                                        <div className="min-w-0">
                                            <h3 className="font-black text-title text-ink leading-tight truncate flex items-center gap-2">
                                                <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: COLOR[g.estado] }} />{g.cliente}
                                            </h3>
                                            <p className="text-caption font-bold text-muted mt-0.5">
                                                {g.items.length} equipo{g.items.length !== 1 ? 's' : ''} · {g.estado === 'VENCIDO' ? 'vencido' : 'por vencer'}
                                                {g.filtros > 0 && <span className="text-brand-red"> · {g.filtros} filtro{g.filtros !== 1 ? 's' : ''}</span>}
                                            </p>
                                        </div>
                                        <span className="shrink-0 text-label font-black text-muted">{g.mesesMax} meses</span>
                                    </div>
                                    {(g.ofrecidoEn || g.conPortal) && (
                                        <div className="flex flex-wrap gap-1.5">
                                            {g.ofrecidoEn && (
                                                <span className={`inline-flex items-center gap-1 h-6 px-2 rounded-full text-label font-black ${g.ofrecidoReciente ? 'bg-[var(--success-bg)] text-[var(--success-tx)]' : 'bg-chip text-muted'}`}>
                                                    <LuCheck size={12} /> Ofrecido {haceCuanto(g.ofrecidoEn)}
                                                </span>
                                            )}
                                            {g.conPortal && (
                                                <span className="inline-flex items-center gap-1 h-6 px-2 rounded-full bg-chip text-label font-bold text-muted">
                                                    <LuBuilding2 size={12} /> Le avisa el portal
                                                </span>
                                            )}
                                        </div>
                                    )}
                                    <div className="rounded-xl bg-panel divide-y divide-black/[0.05] dark:divide-white/[0.05]">
                                        {(abiertos[g.key] ? g.items : g.items.slice(0, 4)).map((a, i) => {
                                            const esFiltro = a.tipoAlerta === 'FILTRO';
                                            const fechaRef = esFiltro && a.fechaUltimoFiltro ? a.fechaUltimoFiltro : a.fechaUltimoServicio;
                                            return (
                                                <div key={`${a.serial}-${i}`} className="flex items-center gap-2 px-3 py-2 text-caption">
                                                    <span className="w-2 h-2 rounded-full shrink-0" style={{ background: COLOR[a.estado] }} />
                                                    <span className="flex-1 min-w-0 truncate">
                                                        <span className="font-bold text-ink">{a.serial}</span>
                                                        <span className="text-muted"> · {a.sedeNombre}</span>
                                                    </span>
                                                    <span className="shrink-0 text-muted">{esFiltro ? 'Filtro' : 'Service'} {fechaAR(fechaRef)}</span>
                                                </div>
                                            );
                                        })}
                                        {g.items.length > 4 && (
                                            <button type="button" onClick={() => setAbiertos(o => ({ ...o, [g.key]: !o[g.key] }))}
                                                className="w-full px-3 py-2 text-left text-caption font-black text-secondary">
                                                {abiertos[g.key] ? 'Ver menos' : `Ver los ${g.items.length} equipos`}
                                            </button>
                                        )}
                                    </div>
                                    <div className="flex gap-2">
                                        {onCrearTrabajo && g.clienteId && (
                                            <button onClick={() => onCrearTrabajo({ id: g.clienteId, nombre: g.cliente })}
                                                className="flex-1 h-12 rounded-xl bg-chip text-ink text-body font-black flex justify-center items-center gap-2 active:scale-[0.98]">
                                                <LuWrench size={16} /> Crear trabajo
                                            </button>
                                        )}
                                        <button onClick={() => ofrecer(g)}
                                            className={`flex-1 h-12 rounded-xl text-body font-black flex justify-center items-center gap-2 active:scale-[0.98] ${g.ofrecidoReciente ? 'bg-chip text-secondary' : 'bg-[#25D366] text-white'}`}>
                                            <LuMessageCircle size={16} /> {g.ofrecidoReciente ? 'Volver a ofrecer' : 'Ofrecer'}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}
